/**
 * Email Service
 * Provides functions for fetching organization users and sending email reports.
 */

import { apiGet } from './apiService';

// Email server configuration
// For development, the email server runs on localhost:3001
// For production, update this URL to the deployed server
const EMAIL_SERVER_URL = process.env.REACT_APP_EMAIL_SERVER_URL || 'http://localhost:3001';

/**
 * Fetch all users for the current organization
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const fetchOrganizationUsers = async () => {
  try {
    const result = await apiGet('/user', {
      headers: {
        'Accept': '*/*',
      },
    });

    if (result.success) {
      // Handle different response structures
      let users = [];
      if (Array.isArray(result.data)) {
        users = result.data;
      } else if (result.data && Array.isArray(result.data.data)) {
        users = result.data.data;
      } else if (result.data && Array.isArray(result.data.users)) {
        users = result.data.users;
      }

      // Map users to a simplified format with required fields
      const mappedUsers = users.map(user => ({
        _id: user._id,
        name: user.name || 'Unknown',
        email: user.email || '',
        phoneNumber: user.phoneNumber ? 
          `+${user.phoneNumber.code || ''} ${user.phoneNumber.phone || ''}` : '',
        isActive: user.isActive !== false,
        roles: user.roles ? user.roles.map(r => r.role).join(', ') : '',
        locations: user.locations ? user.locations.map(l => l.name).join(', ') : '',
        allLocationAccess: user.allLocationAccess || false,
      }));

      // Filter to only active users with email addresses
      const activeUsersWithEmail = mappedUsers.filter(
        user => user.isActive && user.email
      );

      // Deduplicate by user ID
      const uniqueUsers = [];
      const seenIds = new Set();
      for (const user of activeUsersWithEmail) {
        if (!seenIds.has(user._id)) {
          seenIds.add(user._id);
          uniqueUsers.push(user);
        }
      }

      return {
        success: true,
        data: uniqueUsers,
        allUsers: mappedUsers, // Include all users for reference
      };
    }

    return result;
  } catch (error) {
    console.error('Error fetching organization users:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch users',
    };
  }
};

/**
 * Convert an image URL or file to base64
 * @param {string} imageUrl - URL of the image
 * @returns {Promise<string>} Base64 encoded image
 */
export const imageToBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Extract base64 data (remove the data:image/xxx;base64, prefix)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return null;
  }
};

/**
 * Convert a PDF blob to base64
 * @param {Blob} pdfBlob - PDF blob
 * @returns {Promise<string>} Base64 encoded PDF
 */
export const pdfBlobToBase64 = (pdfBlob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Extract base64 data (remove the data:application/pdf;base64, prefix)
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(pdfBlob);
  });
};

/**
 * Send email report with PDF attachment
 * @param {Object} params - Email parameters
 * @param {Array<{email: string, name: string}>} params.recipients - List of recipients
 * @param {string} params.subject - Email subject
 * @param {string} params.htmlBody - HTML email body
 * @param {string} params.pdfBase64 - Base64 encoded PDF
 * @param {string} params.pdfFilename - Filename for the PDF attachment
 * @param {string} [params.logoBase64] - Base64 encoded logo image
 * @param {string} [params.bannerBase64] - Base64 encoded banner image
 * @returns {Promise<{success: boolean, message?: string, results?: Array, errors?: Array, error?: string}>}
 */
export const sendEmailWithAttachment = async ({
  recipients,
  subject,
  htmlBody,
  pdfBase64,
  pdfFilename,
  logoBase64,
  bannerBase64,
}) => {
  try {
    const response = await fetch(`${EMAIL_SERVER_URL}/api/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recipients,
        subject,
        htmlBody,
        pdfBase64,
        pdfFilename,
        logoBase64,
        bannerBase64,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `Server error: ${response.status}`,
      };
    }

    return result;
  } catch (error) {
    console.error('Error sending email:', error);
    return {
      success: false,
      error: error.message || 'Failed to send email. Is the email server running?',
    };
  }
};

/**
 * Check if the email server is running
 * @returns {Promise<boolean>}
 */
export const checkEmailServerHealth = async () => {
  try {
    const response = await fetch(`${EMAIL_SERVER_URL}/api/health`, {
      method: 'GET',
    });
    
    if (response.ok) {
      const result = await response.json();
      return result.status === 'ok';
    }
    return false;
  } catch (error) {
    console.error('Email server health check failed:', error);
    return false;
  }
};

/**
 * Send a test email
 * @param {string} email - Test email address
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const sendTestEmail = async (email) => {
  try {
    const response = await fetch(`${EMAIL_SERVER_URL}/api/test-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `Server error: ${response.status}`,
      };
    }

    return result;
  } catch (error) {
    console.error('Error sending test email:', error);
    return {
      success: false,
      error: error.message || 'Failed to send test email',
    };
  }
};

export default {
  fetchOrganizationUsers,
  imageToBase64,
  pdfBlobToBase64,
  sendEmailWithAttachment,
  checkEmailServerHealth,
  sendTestEmail,
};
